import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { Campaign } from '../models/campaign.model';
import { Page } from '../models/page.model';
import { toLocalDateString } from '../utils/utils';

@Injectable({ providedIn: 'root' })
export class CampaignService {
  private campaignsUrl = '/campaigns';
  constructor(private http: HttpClient) {}

  private campaigns: Campaign[];

  getCampaigns(): Observable<Campaign[]> {
    if (this.campaigns) {
      return of(this.campaigns);
    }

    return this.http.get<any>(this.campaignsUrl + '/all').pipe(
      map((response) => {
        const data: Campaign[] = response.data;
        data.forEach((element: Campaign) => {
          this.handleDates(element);
        });
        this.campaigns = data;
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaign(id: number): Observable<Campaign> {
    return this.http.get<any>(this.campaignsUrl + '/' + id).pipe(
      map((response) => this.handleDates(response.data)),
      catchError(this.handleError),
    );
  }

  getCampaignsAdvanced(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: string,
  ): Observable<Page<Campaign>> {
    let url = this.campaignsUrl + '?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    if (filter) {
      url += '&year=' + filter;
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: Campaign) => {
          this.handleDates(element);
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  save(campaign: Campaign): Observable<Campaign> {
    if (campaign.id) {
      return this.put(campaign);
    }
    return this.post(campaign);
  }

  delete(campaign: Campaign) {
    const url = `${this.campaignsUrl}/${campaign.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  private handleError(error: any) {
    // FIXME
    console.error('An error occurred', error);
    return throwError(() => error.message || error);
  }

  // Add new campaign
  private post(campaign: Campaign): Observable<Campaign> {
    return this.http.post<any>(this.campaignsUrl, this.serialize(campaign)).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  // Update existing campaign
  private put(campaign: Campaign) {
    const url = `${this.campaignsUrl}/${campaign.id}`;

    return this.http.put<any>(url, this.serialize(campaign)).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private serialize(campaign: Campaign): any {
    return {
      ...campaign,
      startingOn: toLocalDateString(campaign.startingOn),
      endingOn: toLocalDateString(campaign.endingOn),
    };
  }

  private handleDates(campaign: Campaign): Campaign {
    campaign.startingOn = new Date(<string>campaign.startingOn);
    campaign.endingOn = new Date(<string>campaign.endingOn);
    return campaign;
  }
}
